import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { GraduationCap, Target, Users, Loader2, X, Info } from 'lucide-react';
import { trackEvent, EVENT_TYPE_PAGE } from '../utils/trackEvent.jsx';
import { fetchResourceCatalog } from '../services/resourceLibraryAPI.js';
import ResourceCard from '../components/resource-library/ResourceCard.jsx';
import {
  LEGACY_CATALOG_REDIRECT_MESSAGES,
  PROFESSIONAL_DEVELOPMENT_FROM_PARAM,
} from '../constants/professionalDevelopment.js';

const HERO_CONTENT = {
  title: 'Islamic Financing Professional Development',
  description:
    'Curated modules that give your team the product knowledge, language, and confidence to originate and explain Islamic Financing.',
};

const PERSONAS = [
  {
    icon: Target,
    label: 'Sales',
    title: 'Sales',
    text: 'Build the product knowledge and pitch language your team needs to win partner deals.',
  },
  {
    icon: Users,
    label: 'Marketing',
    title: 'Marketing teams',
    text: 'Translate product detail into messaging your audience understands and believes.',
  },
  {
    icon: GraduationCap,
    label: 'Students',
    title: 'Finance students',
    text: 'Build a working foundation in Islamic Financing you can apply in class and on the job.',
  },
];

function AudienceSelector({ personas, activeIndex, onSelect }) {
  const active = personas[activeIndex];

  return (
    <div className="mt-8 max-w-2xl mx-auto">
      <p className="text-xs font-semibold uppercase tracking-widest text-emerald-200/80 mb-4">
        Built for your team
      </p>
      <div
        role="tablist"
        aria-label="Audience"
        className="inline-flex flex-wrap justify-center gap-1 rounded-full border border-white/15 bg-white/10 p-1 backdrop-blur-sm"
      >
        {personas.map(({ icon: Icon, label, title }, index) => {
          const isActive = index === activeIndex;
          return (
            <button
              key={title}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls="audience-panel"
              id={`audience-tab-${index}`}
              onClick={() => onSelect(index)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-all ${
                isActive
                  ? 'bg-white text-emerald-900 shadow-sm'
                  : 'text-emerald-50 hover:bg-white/10'
              }`}
            >
              <Icon className="h-4 w-4 shrink-0" aria-hidden />
              {label}
            </button>
          );
        })}
      </div>
      <p
        id="audience-panel"
        role="tabpanel"
        aria-labelledby={`audience-tab-${activeIndex}`}
        className="mt-5 text-base md:text-lg text-emerald-100/90 leading-relaxed transition-opacity"
      >
        {active.text}
      </p>
    </div>
  );
}

function ResourceLibrary() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [activePersona, setActivePersona] = useState(0);

  const legacyFrom = searchParams.get(PROFESSIONAL_DEVELOPMENT_FROM_PARAM);
  const legacyMessage = legacyFrom ? LEGACY_CATALOG_REDIRECT_MESSAGES[legacyFrom] : null;

  const dismissLegacyNotice = () => {
    const next = new URLSearchParams(searchParams);
    next.delete(PROFESSIONAL_DEVELOPMENT_FROM_PARAM);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    trackEvent(EVENT_TYPE_PAGE);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const list = await fetchResourceCatalog();
        if (!cancelled) setPackages(list);
      } catch (e) {
        if (!cancelled) setLoadError(e.message || 'Could not load packages');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-r from-[#f8fafc] to-[#e2e8f0]">
      <section className="bg-gradient-to-br from-[#1a4a1a] via-emerald-800 to-emerald-900 text-white py-12 md:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl md:text-5xl font-bold leading-tight mb-6">
            {HERO_CONTENT.title}
          </h1>
          <p className="text-lg md:text-xl text-emerald-100/90 max-w-2xl mx-auto leading-relaxed">
            {HERO_CONTENT.description}
          </p>
          <AudienceSelector
            personas={PERSONAS}
            activeIndex={activePersona}
            onSelect={setActivePersona}
          />
        </div>
      </section>

      {legacyMessage && (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <div
            role="status"
            className="flex gap-3 items-start rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-950"
          >
            <Info className="w-5 h-5 shrink-0 text-emerald-700 mt-0.5" aria-hidden />
            <p className="flex-1 leading-relaxed">{legacyMessage}</p>
            <button
              type="button"
              onClick={dismissLegacyNotice}
              className="shrink-0 rounded-md p-1 text-emerald-800 hover:bg-emerald-100 transition-colors"
              aria-label="Dismiss notice"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-[#1a4a1a] mb-4">Learning packages</h2>
          <p className="text-slate-600 max-w-xl mx-auto">
            Each package includes modules, key takeaways, and practical language you can use today.
          </p>
        </div>

        {loading && (
          <div className="flex justify-center py-16 text-emerald-800">
            <Loader2 className="w-8 h-8 animate-spin" />
          </div>
        )}

        {loadError && !loading && (
          <p className="text-center text-amber-800 text-sm mb-8">{loadError}</p>
        )}

        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {packages.map((resource) => (
              <ResourceCard key={resource.slug} resource={resource} />
            ))}
          </div>
        )}

        {!loading && packages.length === 0 && (
          <p className="text-center text-slate-500">No packages published yet.</p>
        )}
      </section>

      <section className="bg-white border-t border-slate-200 py-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-xl font-bold text-[#1a4a1a] mb-3">Need a custom package for your institution?</h2>
          <p className="text-slate-600 mb-6">
            Team licenses, CDFI onboarding kits, and bespoke curriculum available.
          </p>
          <Link
            to="/consult"
            className="inline-flex items-center justify-center bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-8 py-3 rounded-xl transition-colors"
          >
            Talk to us
          </Link>
        </div>
      </section>
    </div>
  );
}

export default ResourceLibrary;
